import { startOfDay } from 'date-fns';
import { observer } from 'mobx-react';
import React from 'react';
import { DatePicker } from '@components/date-picker';
import { Label } from '@components/label';
import { RadioGroup } from '@components/radio-group';
import { SectionHeading } from '@components/section-heading';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Label as FieldLabel } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import {
    ActivityType,
    ChallengeAggregationType,
    ChallengeFitnessMetricType,
    ChallengeInitialResultMode,
    ChallengeRuleType
} from '@core/api/ChallengeClient';
import { injectTSDI } from '@core/tsdi';
import { DEFAULT_TIME_ZONE, getTimezoneOptions } from '@components/utils/time';
import { toZonedDateTime } from '@ms-rewards/date-lib';
import { ChallengeConsentsStore } from '@src/communities/community/challenges/challenge-form/consents/store';
import PersonalRules from '@src/communities/community/challenges/challenge-form/rules/personal-rules';
import { ChallengeStore } from '@src/communities/community/challenges/challenge-form/store';
import { workoutCategories } from '@src/communities/community/goals';
import {
    challengeTargetUnit,
    COMPOSITE_POINTS_RULE_TYPE,
    isRawValueRuleType,
    supportsActivityFilters
} from '../../rule-types';
import { ChallengeDataStore } from '../data-store';
import { ChallengeActivityFiltersEditor } from './activity-filters';
import { ChallengeTypeSelect } from './challenge-type';
import GroupRules from './group-rules/component';
import {
    aggregationOptions,
    fitnessMetricOptions,
    initialResultModeOptions,
    normalizationOperationOptions,
    normalizationOutputFormatOptions,
    ruleTypeOptions
} from './options';
import { ChallengeRulesStore } from './store';
import { ChallengeTarget } from './target';
import TeamRules from './team-rules';

const SwitchRow = ({
    id,
    label,
    checked,
    disabled,
    onChange
}: {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange(checked: boolean): void;
}) => (
    <div className="flex items-center gap-2">
        <Switch
            id={id}
            name={id}
            checked={checked}
            onCheckedChange={onChange}
            disabled={disabled}
        />
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
    </div>
);

export const RulesStep = observer(() => {
    const { disabledRulesEdit } = injectTSDI(ChallengeDataStore);
    const { createMode } = injectTSDI(ChallengeStore);
    const { enableConsents, disableConsents } = injectTSDI(
        ChallengeConsentsStore
    );
    const rulesStore = injectTSDI(ChallengeRulesStore);
    const {
        fields: {
            timeFrom,
            timeTo,
            activityType,
            workoutCategory,
            useCompositePoints,
            compositePointTypeKey,
            ruleType,
            fitnessMetricType,
            calculatedMetricKey,
            aggregationType,
            initialResultMode,
            resultBaseline,
            normalizationOperation,
            normalizationOperand,
            normalizationOutputFormat,
            activityFilters,
            timeZone,
            joinDeadline,
            autoJoinEnabled
        },

        validateRules
    } = rulesStore;

    const ruleTypeValue = useCompositePoints.value
        ? COMPOSITE_POINTS_RULE_TYPE
        : ruleType.value;

    const initialResultModeLocked =
        ruleType.value !== ChallengeRuleType.ACTIVITY_POINTS;

    const usesCalculatedMetric =
        isRawValueRuleType(ruleType.value) &&
        fitnessMetricType.value ===
            ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC;

    return (
        <div className="flex flex-col gap-8">
            <section className="flex flex-col gap-4">
                <SectionHeading
                    title="What members have to do"
                    hint="The rule type decides what the target number means."
                />
                <ChallengeTypeSelect disabled={disabledRulesEdit} />
                <div className="grid gap-4 md:grid-cols-2">
                    <Select
                        label="Rule type"
                        value={ruleTypeValue}
                        options={ruleTypeOptions}
                        onChange={(value) => {
                            if (value === COMPOSITE_POINTS_RULE_TYPE) {
                                useCompositePoints.onChange(true);
                                ruleType.onChange(
                                    ChallengeRuleType.ACTIVITY_POINTS
                                );
                            } else {
                                useCompositePoints.onChange(false);
                                compositePointTypeKey.onChange(undefined);
                                ruleType.onChange(value);
                            }
                            validateRules();
                        }}
                        disabled={disabledRulesEdit}
                    />
                    {useCompositePoints.value && (
                        <Select
                            label="Composite point type"
                            value={compositePointTypeKey.value ?? ''}
                            options={rulesStore.compositePointDefinitions.map(
                                (def) => ({
                                    label: def.pointTypeKey,
                                    value: def.pointTypeKey
                                })
                            )}
                            onChange={(value) => {
                                compositePointTypeKey.onChange(
                                    value === '' ? undefined : value
                                );
                                validateRules();
                            }}
                            disabled={disabledRulesEdit}
                            error={compositePointTypeKey.error as string}
                        />
                    )}
                </div>
                {isRawValueRuleType(ruleType.value) && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <Select
                            label="Fitness metric"
                            value={fitnessMetricType.value}
                            options={fitnessMetricOptions}
                            onChange={(value) => {
                                fitnessMetricType.onChange(value);
                                if (
                                    value ===
                                        ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC &&
                                    aggregationType.value ===
                                        ChallengeAggregationType.SUM
                                ) {
                                    aggregationType.onChange(
                                        ChallengeAggregationType.MIN
                                    );
                                }
                                validateRules();
                            }}
                            disabled={disabledRulesEdit}
                        />
                        {usesCalculatedMetric && (
                            <Select
                                label="Calculated metric"
                                value={calculatedMetricKey.value ?? ''}
                                options={rulesStore.calculatedMetricDefinitions.map(
                                    (definition) => ({
                                        label: `${definition.label} (${definition.metricKey})`,
                                        value: definition.metricKey
                                    })
                                )}
                                onChange={(value) => {
                                    rulesStore.selectCalculatedMetric(
                                        value === '' ? undefined : value
                                    );
                                    validateRules();
                                }}
                                disabled={disabledRulesEdit}
                                error={calculatedMetricKey.error as string}
                            />
                        )}
                        <Select
                            label="Aggregation type"
                            value={aggregationType.value}
                            options={aggregationOptions}
                            onChange={(value) => {
                                aggregationType.onChange(value);
                                validateRules();
                            }}
                            disabled={disabledRulesEdit}
                        />
                        <div className="flex flex-col gap-1.5">
                            <Select
                                label="Display normalization"
                                value={normalizationOperation.value}
                                options={normalizationOperationOptions}
                                onChange={(value) => {
                                    normalizationOperation.onChange(value);
                                    validateRules();
                                }}
                                disabled={disabledRulesEdit}
                            />
                            <Label>
                                Optional. Rescales the stored result for display
                                only — ranking is unaffected. A 5 km pace race
                                multiplies by 5 and shows a race time.
                            </Label>
                        </div>
                        {normalizationOperation.value && (
                            <>
                                <TextField
                                    type="number"
                                    label="Multiplier"
                                    value={normalizationOperand.value ?? ''}
                                    onChange={(value) => {
                                        normalizationOperand.onChange(
                                            value === ''
                                                ? undefined
                                                : Number(value)
                                        );
                                        validateRules();
                                    }}
                                    error={normalizationOperand.error}
                                    InputLabelShrink
                                    data-testid="normalization-operand-challenge-rule-field"
                                    disabled={disabledRulesEdit}
                                />
                                <Select
                                    label="Displayed as"
                                    value={normalizationOutputFormat.value}
                                    options={normalizationOutputFormatOptions}
                                    onChange={(value) => {
                                        normalizationOutputFormat.onChange(
                                            value
                                        );
                                        validateRules();
                                    }}
                                    disabled={disabledRulesEdit}
                                />
                            </>
                        )}
                    </div>
                )}
                {ruleType.value === ChallengeRuleType.ACTIVITY_COUNT && (
                    <Label>
                        Scores one point per qualifying activity; the result is
                        the number of activities.
                    </Label>
                )}
                <ChallengeTarget disabled={disabledRulesEdit} />
                {supportsActivityFilters(ruleType.value) && (
                    <ChallengeActivityFiltersEditor
                        value={activityFilters.value}
                        onChange={(filters) => {
                            activityFilters.onChange(filters);
                            validateRules();
                        }}
                        disabled={disabledRulesEdit}
                    />
                )}
                {!useCompositePoints.value &&
                    ruleType.value === ChallengeRuleType.ACTIVITY_POINTS && (
                        <>
                            <RadioGroup<ActivityType | undefined>
                                label="Activity types"
                                options={[
                                    { label: 'All', value: undefined },
                                    {
                                        label: 'Steps',
                                        value: ActivityType.DAILY_STEPS
                                    },
                                    {
                                        label: 'Facility visit',
                                        value: ActivityType.FACILITY_VISIT
                                    },
                                    {
                                        label: 'Workout',
                                        value: ActivityType.WORKOUT
                                    }
                                ]}
                                value={activityType.value}
                                onChange={(value) => {
                                    activityType.onChange(value);
                                    validateRules();
                                }}
                                error={activityType.error}
                                disabled={disabledRulesEdit}
                                dataTestId="challenge-activity-types"
                            />
                            {activityType.value === ActivityType.WORKOUT && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <Select
                                        value={workoutCategory.value}
                                        onChange={(value) =>
                                            workoutCategory.onChange(value)
                                        }
                                        options={workoutCategories.map(
                                            (workoutCategory) => ({
                                                label: workoutCategory.label,
                                                value: workoutCategory.value
                                            })
                                        )}
                                        disabled={disabledRulesEdit}
                                        label="Workout category"
                                    />
                                </div>
                            )}
                        </>
                    )}
            </section>

            <section className="flex flex-col gap-4">
                <SectionHeading title="When it runs" />
                <Select
                    label="Timezone"
                    value={timeZone.value}
                    options={getTimezoneOptions()}
                    onChange={(value) => {
                        timeZone.onChange(value);
                    }}
                    disabled={disabledRulesEdit}
                />
                <div className="grid gap-4 md:grid-cols-2">
                    <DatePicker
                        label="Start date"
                        minDate={startOfDay(new Date())}
                        value={timeFrom.value ? timeFrom.value.toDate() : null}
                        onChange={(value) => {
                            void timeFrom.onChange(
                                value
                                    ? toZonedDateTime(value, DEFAULT_TIME_ZONE)
                                    : undefined
                            );
                            validateRules();
                        }}
                        error={(timeFrom.error as string) || undefined}
                        disabled={disabledRulesEdit}
                    />
                    <DatePicker
                        label="End date"
                        value={timeTo.value ? timeTo.value.toDate() : null}
                        minDate={
                            timeFrom.value ? timeFrom.value.toDate() : undefined
                        }
                        onChange={(value) => {
                            if (!value) {
                                void timeTo.onChange(undefined);
                            } else {
                                const endOfDay = new Date(value);
                                endOfDay.setHours(23, 59, 59, 999);
                                void timeTo.onChange(
                                    toZonedDateTime(endOfDay, DEFAULT_TIME_ZONE)
                                );
                            }
                            validateRules();
                        }}
                        error={(timeTo.error as string) || undefined}
                        disabled={disabledRulesEdit}
                    />
                </div>
            </section>

            <section className="flex flex-col gap-4">
                <SectionHeading
                    title="Joining"
                    hint="Who can still join once the challenge is running, and what they start with."
                />
                <div className="grid gap-4 md:grid-cols-2">
                    <DatePicker
                        label="Join deadline"
                        value={
                            joinDeadline.value
                                ? joinDeadline.value.toDate()
                                : null
                        }
                        minDate={
                            timeFrom.value ? timeFrom.value.toDate() : undefined
                        }
                        onChange={(value) => {
                            void joinDeadline.onChange(
                                value
                                    ? toZonedDateTime(value, DEFAULT_TIME_ZONE)
                                    : undefined
                            );
                            validateRules();
                        }}
                        clearable
                        error={(joinDeadline.error as string) || undefined}
                        disabled={disabledRulesEdit}
                    />
                    <div className="flex flex-col gap-1.5">
                        <Select
                            label="Joining mid-challenge starts you with"
                            value={
                                initialResultModeLocked
                                    ? ChallengeInitialResultMode.INCREMENTAL
                                    : initialResultMode.value
                            }
                            options={initialResultModeOptions}
                            onChange={(value) => {
                                initialResultMode.onChange(value);
                                validateRules();
                            }}
                            disabled={
                                disabledRulesEdit || initialResultModeLocked
                            }
                        />
                        {initialResultModeLocked && (
                            <Label>
                                Only activity points challenges can seed a
                                joiner with the current day.
                            </Label>
                        )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            type="number"
                            label="Result baseline"
                            value={resultBaseline.value ?? ''}
                            onChange={(value) => {
                                resultBaseline.onChange(
                                    value === '' ? undefined : Number(value)
                                );
                                validateRules();
                            }}
                            InputLabelShrink
                            data-testid="result-baseline-challenge-rule-field"
                            disabled={disabledRulesEdit}
                        />
                        <Label>
                            Optional. New members start at this value, in{' '}
                            {challengeTargetUnit(
                                ruleType.value,
                                fitnessMetricType.value
                            )}
                            , instead of at zero. Leave empty to start them at
                            zero.
                        </Label>
                    </div>
                </div>
                <SwitchRow
                    id="autoJoinEnabled"
                    label="Auto join enabled"
                    checked={autoJoinEnabled.value}
                    onChange={(checked) => {
                        autoJoinEnabled.onChange(checked);
                        validateRules();
                        if (checked) {
                            disableConsents();
                        } else {
                            enableConsents();
                        }
                    }}
                    disabled={!createMode}
                />
            </section>

            <section className="flex flex-col gap-4">
                <PersonalRules />
                <TeamRules />
                <GroupRules />
            </section>
        </div>
    );
});
